import { Button, Kind, Size } from '@thanx/uikit/button'
import ConfirmModal from 'components/ConfirmModal'
import { buildTranslate } from 'locales'
import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CmsPage from 'scenes/Cms/components/CmsPage'
import {
  addBottomEmptyTab,
  addTopEmptyTab,
  closeDeleteModal,
  removeNavigationTab,
} from 'slices/Cms/Content/AppNavigation/BottomNav'
import {
  selectBottomNav,
  selectDeleteModal,
  selectMaxTabsReached,
} from 'slices/Cms/Content/AppNavigation/BottomNav/selectors'
import Footer from '../_shared/Footer'
import Sidebar from '../_shared/Sidebar'
import { useSyncApiState } from './hooks'
import TabDescription from './TabDescription'

const t = buildTranslate('cms.content.app_navigation.bottom_nav')
const tBlocks = buildTranslate('cms.components.blocks')

export const BottomNav: React.FC = () => {
  // Sync the local state with the API state (also handles fetching)
  useSyncApiState()

  const dispatch = useDispatch()
  const bottomNavState = useSelector(selectBottomNav)
  const deleteModal = useSelector(selectDeleteModal)
  const maxTabsReached = useSelector(selectMaxTabsReached)

  // Get tabs directly from the hash and sort them by position (memoized for performance)
  const sortedTabs = useMemo(
    () =>
      Object.values(bottomNavState.navigation_tabs).sort(
        (a, b) => a.position - b.position
      ),
    [bottomNavState.navigation_tabs]
  )

  const addTopTab = () => {
    if (!maxTabsReached) {
      dispatch(addTopEmptyTab())
    }
  }
  const addBottomTab = () => {
    if (!maxTabsReached) {
      dispatch(addBottomEmptyTab())
    }
  }

  const handleDelete = () => {
    if (deleteModal.tabId) {
      dispatch(removeNavigationTab(deleteModal.tabId))
      dispatch(closeDeleteModal())
    }
  }

  const handleCloseDeleteModal = () => {
    dispatch(closeDeleteModal())
  }

  return (
    <CmsPage
      title={t('title')}
      description={t('description')}
      sidebar={<Sidebar />}
    >
      <ConfirmModal
        title={tBlocks('delete_modal.title', { type: 'navigation tab' })}
        description={tBlocks('delete_modal.description', {
          type: 'navigation tab',
        })}
        confirmText={tBlocks('delete_modal.confirm', {
          type: 'navigation tab',
        })}
        isOpen={deleteModal.isOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDelete}
      />
      <div className="mb-l">
        {/* Tab List */}
        {sortedTabs.map((tab, index) => (
          <div key={tab.id}>
            <TabDescription tabId={tab.id} index={index} className="mb-l" />
            {/* Add button after first tab (position 1) */}
            {index === 0 && (
              <Button
                kind={Kind.SECONDARY}
                size={Size.STANDARD}
                onClick={addTopTab}
                className={'w-100 mb-m'}
                disabled={maxTabsReached}
              >
                {t('add_new_tab')}
              </Button>
            )}
            {/* Add button before last tab (if we're at second-to-last position) */}
            {index === sortedTabs.length - 2 && sortedTabs.length > 1 && (
              <Button
                kind={Kind.SECONDARY}
                size={Size.STANDARD}
                onClick={addBottomTab}
                className={'w-100 mb-m'}
                disabled={maxTabsReached}
              >
                {t('add_new_tab')}
              </Button>
            )}
          </div>
        ))}
      </div>
      <Footer />
    </CmsPage>
  )
}

export default BottomNav
